// js/app.js — Bootstrap: wires tabs, shows username overlay on first visit

import { seedIfEmpty, seedHolidaysForYear } from './data.js';
import { renderTimesheet, performUndo } from './timesheet.js';
import { renderReports }  from './reports.js';
import { renderSettings } from './settings.js';
import * as Cal from './calendar.js';
import { initTheme } from './theme.js';

// ── Username overlay ──────────────────────────────────────────────────────────

function showUsernameOverlay() {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(10,22,40,0.75);display:flex;align-items:center;justify-content:center;z-index:9999;';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:16px;padding:40px 48px;max-width:400px;width:90%;box-shadow:0 20px 60px rgba(0,0,0,0.35);text-align:center;">
        <img src="img/vimond-logo.png" style="height:36px;margin-bottom:20px;display:block;margin-left:auto;margin-right:auto;">
        <h2 style="font-size:1.4rem;font-weight:700;color:#0A1628;margin-bottom:8px;">Welcome to Timelogger</h2>
        <p style="color:#7889B3;font-size:0.9rem;margin-bottom:24px;">What's your name? This identifies your time entries in the shared log.</p>
        <input id="tl-username-input" type="text" placeholder="e.g. Fredrik"
          style="width:100%;padding:10px 14px;border:1.5px solid #E4E3E0;border-radius:8px;font-size:1rem;margin-bottom:16px;box-sizing:border-box;outline:none;text-align:left;" />
        <button id="tl-username-submit"
          style="width:100%;padding:12px;background:#364A7A;color:#fff;border:none;border-radius:8px;font-size:1rem;font-weight:600;cursor:pointer;">
          Let's go →
        </button>
      </div>
    `;
    document.body.appendChild(overlay);

    const input = overlay.querySelector('#tl-username-input');
    const btn   = overlay.querySelector('#tl-username-submit');

    input.focus();

    const submit = () => {
      const name = input.value.trim();
      if (!name) {
        input.style.borderColor = '#ef4444';
        input.placeholder = 'Please enter your name';
        return;
      }
      localStorage.setItem('tl_username', name);
      overlay.remove();
      resolve(name);
    };

    btn.addEventListener('click', submit);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') submit(); });
    input.addEventListener('input', () => { input.style.borderColor = '#E4E3E0'; });
  });
}

// ── Init ──────────────────────────────────────────────────────────────────────

async function init() {
  initTheme();

  if (!localStorage.getItem('tl_username')) {
    await showUsernameOverlay();
  }

  await seedIfEmpty();

  const thisYear = new Date().getFullYear();
  await seedHolidaysForYear(thisYear);
  await seedHolidaysForYear(thisYear + 1);

  await renderAll();
}

init().catch(err => console.error('TimeLogger init failed:', err));

// ── Tab switching ─────────────────────────────────────────────────────────────

let activeTab = 'timesheet';

document.getElementById('tab-nav').addEventListener('click', async e => {
  const btn = e.target.closest('[data-tab]');
  if (!btn) return;
  activeTab = btn.dataset.tab;
  document.querySelectorAll('.tab-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.tab === activeTab);
  });
  document.querySelectorAll('.view-panel').forEach(p => {
    p.classList.toggle('hidden', !p.id.endsWith(activeTab));
  });
  await renderAll();
});

// ── Global Ctrl+Z / Cmd+Z undo ────────────────────────────────────────────────

document.addEventListener('keydown', e => {
  if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    e.preventDefault();
    performUndo();
  }
});

// ── Global keyboard shortcuts ─────────────────────────────────────────────────

document.addEventListener('keydown', async e => {
  const tag = document.activeElement?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  if (activeTab !== 'timesheet') return;
  if (e.ctrlKey || e.metaKey || e.altKey) return;

  switch (e.key) {
    case 'n':          e.preventDefault(); document.getElementById('btn-add-entry')?.click(); break;
    case 'ArrowLeft':  e.preventDefault(); Cal.goPrev();            await renderAll(); break;
    case 'ArrowRight': e.preventDefault(); Cal.goNext();            await renderAll(); break;
    case 't':          e.preventDefault(); Cal.setDate(new Date()); await renderAll(); break;
    case 'd':          e.preventDefault(); Cal.setView('day');      await renderAll(); break;
    case 'w':          e.preventDefault(); Cal.setView('week');     await renderAll(); break;
    case 'm':          e.preventDefault(); Cal.setView('month');    await renderAll(); break;
    case 'y':          e.preventDefault(); Cal.setView('year');     await renderAll(); break;
  }
});

// ── Render dispatcher ─────────────────────────────────────────────────────────

async function renderAll() {
  if (activeTab === 'timesheet') await renderTimesheet();
  if (activeTab === 'reports')   await renderReports();
  if (activeTab === 'settings')  await renderSettings();
}
